import { useEffect, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

/** Who gets EMAILED when someone asks for a missing article. Everyone else with
 *  KB edit rights still gets the in-app nudge. */
export default function KbRequestOwnerCard() {
  const [data, setData] = useState(null);
  const [owner, setOwner] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/kb/requests/owner").then(({ data: d }) => {
      setData(d);
      setOwner(d.owner_id || "none");
    }).catch(() => setData({ editors: [], can_edit: false }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/kb/requests/owner", { user_id: owner === "none" ? "" : owner });
      toast.success(d.owner_name ? `${d.owner_name} will be emailed new requests` : "Email alerts off");
      setData((prev) => ({ ...prev, owner_id: d.owner_id, owner_name: d.owner_name }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally { setSaving(false); }
  };

  if (!data) return null;
  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="kb-request-owner-card">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-slate-500" />
        <h2 className="font-heading text-base font-semibold text-slate-800">Article request alerts</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Anyone who can open the KB can request a missing article. The KB owner gets an email so
        requests don't wait for someone to log in; other editors get the in-app notification.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select value={owner} onValueChange={setOwner} disabled={!data.can_edit}>
          <SelectTrigger className="w-72" data-testid="kb-request-owner-select">
            <SelectValue placeholder="Nobody — in-app only" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nobody — in-app only</SelectItem>
            {(data.editors || []).map((e) => (
              <SelectItem key={e.user_id} value={e.user_id}>{e.name || e.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={save} disabled={!data.can_edit || saving || owner === (data.owner_id || "none")}
          data-testid="kb-request-owner-save">
          {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save
        </Button>
        {data.owner_name && (
          <span className="text-xs text-slate-500" data-testid="kb-request-owner-current">
            Currently emailing {data.owner_name}
          </span>
        )}
      </div>
    </section>
  );
}
